import { useCallback, useEffect, useState } from "react";
import { useNavigationType } from "react-router-dom";
import type { TablePaginationConfig } from "antd";

const STORAGE_PREFIX = "listPage.";

const loadPage = (fullKey: string): number => {
  try {
    const page = Number(sessionStorage.getItem(fullKey));
    if (Number.isInteger(page) && page >= 1) return page;
  } catch (error) {
    console.warn("Failed to load table page from sessionStorage:", error);
  }
  return 1;
};

const savePage = (fullKey: string, page: number) => {
  try {
    sessionStorage.setItem(fullKey, String(page));
  } catch (error) {
    console.warn("Failed to save table page to sessionStorage:", error);
  }
};

/**
 * Drops every remembered list page. Called from signOut so the next admin on
 * this browser tab doesn't inherit the previous session's list positions.
 */
export const clearRememberedTablePages = () => {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.warn("Failed to clear table pages from sessionStorage:", error);
  }
};

/**
 * Remembers a list Table's page number so BROWSER BACK (or forward) returns
 * to the page the admin left. Any other way into the list — sidebar link,
 * in-app push, fresh tab — is a fresh visit and starts at page 1 (matching
 * how search/filter/sort already reset), so the restore only happens where
 * the admin expects continuity. A page refresh also restores (the browser
 * reports it like a history pop), and logout clears all stored pages via
 * clearRememberedTablePages.
 *
 * Storage is sessionStorage under "listPage.<storageKey>" (per browser tab).
 * antd forces `current` back to 1 on any filter change and preserves it on
 * sort, both through the Table-level `onChange`, so the stored page follows
 * automatically; an oversized restored page is clamped by antd to the last
 * page of the (filtered) data.
 *
 * Pass no `storageKey` to disable remembering (for components that are also
 * rendered in places where page memory is unwanted): `current: undefined`
 * leaves the Table's pagination uncontrolled.
 *
 * Wire as:
 *   pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false }}
 *   onChange={handleTableChange}
 */
export const useRememberedTablePage = (storageKey?: string) => {
  const navigationType = useNavigationType();
  const fullKey = storageKey ? STORAGE_PREFIX + storageKey : undefined;

  // "POP" covers back/forward and the initial document load (incl. refresh);
  // "PUSH"/"REPLACE" are fresh in-app visits.
  const [page, setPage] = useState<number | undefined>(() => {
    if (!fullKey) return undefined;
    return navigationType === "POP" ? loadPage(fullKey) : 1;
  });

  useEffect(() => {
    // A fresh visit must also RESET the stored page, so a later Back can't
    // resurrect a page number from before this visit.
    if (fullKey && navigationType !== "POP") savePage(fullKey, 1);
    // Mount-only: the navigation type that created this mount is what counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      if (!fullKey) return;
      const next = pagination.current ?? 1;
      setPage(next);
      savePage(fullKey, next);
    },
    [fullKey],
  );

  return { page, handleTableChange };
};
